package com.arena_web.core.viewmodel

import com.arena_web.data.dto.*
import com.arena_web.domain.repositories.CardRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class CardShopUiState(
    val myCards: List<UserCardDto> = emptyList(),
    val shopCards: List<UserCardDto> = emptyList(),
    val isLoading: Boolean = true,
    val purchasing: Boolean = false,
    val purchaseError: String? = null,
    val loadError: String? = null,
    /** [autoClaimShop] in flight. */
    val autoClaiming: Boolean = false,
    /** A silent claim failed — the UI falls back to the manual shop row. */
    val autoClaimFailed: Boolean = false,
)

class CardShopViewModel(
    private val cardRepository: CardRepository,
    // When set, the shop renders THIS user's collection (owned + available
    // shop slots) instead of the viewer's own — used on public profiles so
    // visitors can see what cards another player has unlocked.
    private val profileUserId: String? = null,
) : BaseViewModel() {

    private val _uiState = MutableStateFlow(CardShopUiState())
    val uiState: StateFlow<CardShopUiState> = _uiState.asStateFlow()

    override fun onCreate() {
        super.onCreate()
        loadData()
    }

    fun reload() {
        _uiState.value = CardShopUiState()
        loadData()
    }

    fun loadData() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, loadError = null) }

            val fetch = if (profileUserId != null) {
                cardRepository.getUserCards(profileUserId)
            } else {
                cardRepository.getMyCards()
            }
            fetch
                .onSuccess { cards ->
                    _uiState.update {
                        it.copy(
                            myCards = cards.filter { c -> c.isOwned },
                            shopCards = cards.filter { c -> !c.isOwned },
                        )
                    }
                }
                .onFailure { e ->
                    logger.error("Failed to load cards: ${e.message}")
                    _uiState.update { it.copy(loadError = "Failed to load cards: ${e.message}") }
                }

            _uiState.update { it.copy(isLoading = false) }
        }
    }

    fun purchaseCard(templateId: String) {
        viewModelScope.launch {
            _uiState.update { it.copy(purchasing = true, purchaseError = null) }

            cardRepository.purchaseCard(PurchaseCardRequest(templateId))
                .onSuccess { cards ->
                    _uiState.update {
                        it.copy(
                            myCards = cards.filter { c -> c.isOwned },
                            shopCards = cards.filter { c -> !c.isOwned },
                            purchasing = false,
                        )
                    }
                }
                .onFailure { e ->
                    _uiState.update { it.copy(purchaseError = e.message, purchasing = false) }
                }
        }
    }

    /**
     * The access subscription already includes every card, so a manual (free)
     * "purchase" per template is pointless: claim the whole shop silently and
     * let the cards land in the owned list. Callers trigger this only while
     * the viewer is subscribed; while subscribed the shop UI stays hidden
     * unless a claim failed ([CardShopUiState.autoClaimFailed]) — then the
     * manual purchase flow re-appears as a fallback. When the subscription
     * lapses the backend freezes the claimed cards (`frozen`).
     */
    fun autoClaimShop() {
        if (_uiState.value.autoClaiming) return
        val shop = _uiState.value.shopCards
        if (shop.isEmpty()) return
        viewModelScope.launch {
            _uiState.update { it.copy(autoClaiming = true) }
            var failed = false
            for (card in shop) {
                cardRepository.purchaseCard(PurchaseCardRequest(card.templateId))
                    .onSuccess { cards ->
                        _uiState.update {
                            it.copy(
                                myCards = cards.filter { c -> c.isOwned },
                                shopCards = cards.filter { c -> !c.isOwned },
                            )
                        }
                    }
                    .onFailure { e ->
                        logger.error("Auto-claim failed for ${card.templateId}: ${e.message}")
                        failed = true
                    }
            }
            _uiState.update { it.copy(autoClaiming = false, autoClaimFailed = failed) }
        }
    }

    /**
     * Selecting an "active" owned template is now a client-side concept —
     * it picks which template the shop carousel highlights. The backend no
     * longer has a single active card; the user's main profile card is
     * derived from the profile-card gallery.
     */
    fun setActiveCard(cardId: String): String = cardId

    fun clearPurchaseError() {
        _uiState.update { it.copy(purchaseError = null) }
    }
}